import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/Button";
import { Label } from "@/components/ui/Label";
import { Textarea } from "@/components/ui/Textarea";
import { ToolPageLayout } from "@/components/ToolPageLayout";
import { Copy, Sparkles, Check, FileDown, Layers, Link } from "lucide-react";
import { copyToClipboard } from "@/lib/utils";

export function SvgToDataUri() {
  const [svgInput, setSvgInput] = useState(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="100" height="100">
  <circle cx="50" cy="50" r="40" fill="#a855f7" />
  <rect x="35" y="35" width="30" height="30" rx="5" fill="#ffffff" />
</svg>`);

  const [outputs, setOutputs] = useState({
    base64Uri: "",
    utf8Uri: "",
    cssBackground: "",
    htmlImg: ""
  });

  const [copiedType, setCopiedType] = useState<string | null>(null);

  const convertSvgToDataUri = () => {
    if (!svgInput.trim()) {
      setOutputs({ base64Uri: "", utf8Uri: "", cssBackground: "", htmlImg: "" });
      return;
    }

    const cleanSvg = svgInput.trim();

    // 1. Generate Base64 String
    const base64Content = btoa(unescape(encodeURIComponent(cleanSvg)));
    const base64Uri = `data:image/svg+xml;base64,${base64Content}`;

    // 2. Generate UTF-8 URL encoded String (lighter than Base64 because it encodes fewer chars)
    const utf8Encoded = encodeURIComponent(cleanSvg)
      .replace(/%20/g, " ")
      .replace(/%3D/g, "=")
      .replace(/%2F/g, "/")
      .replace(/%3B/g, ";")
      .replace(/%2C/g, ",")
      .replace(/%3A/g, ":");
    const utf8Uri = `data:image/svg+xml;utf8,${utf8Encoded}`;

    // 3. Generate CSS Background snippet
    const cssBackground = `background-image: url("${utf8Uri}");`;

    // 4. Generate HTML Img snippet
    const htmlImg = `<img src="${base64Uri}" alt="Inline Vector Graphic" />`;

    setOutputs({
      base64Uri,
      utf8Uri,
      cssBackground,
      htmlImg
    });
  };

  useEffect(() => {
    convertSvgToDataUri();
  }, [svgInput]);

  const handleCopy = (text: string, type: string) => {
    copyToClipboard(text).then(() => {
      setCopiedType(type);
      setTimeout(() => setCopiedType(null), 2000);
    });
  };

  return (
    <ToolPageLayout
      title="SVG to Data URI Converter - Encode SVG Online Free"
      description="Convert SVG files into copyable Base64 or UTF-8 encoded Data URIs online. Export vector code directly into inline HTML images or CSS background-image declarations."
      h1="SVG to Data URI"
      category="SVG Tools"
      categoryPath="/svg-tools"
      howToUse={[
        "Type or paste your raw, well-formed SVG code into the text area.",
        "The tool instantly encodes and compiles multiple output formats: Base64 URI, UTF-8 URI, CSS, and HTML snippets.",
        "Identify the format that matches your framework and click its Copy button.",
        "Paste the generated string directly into CSS files, HTML documents, or Javascript state variables."
      ]}
      faq={[
        { question: "What is an SVG to Data URI converter?", answer: "An SVG to Data URI converter translates standard XML-based SVG code strings into standard inline Data Schemes (Base64 binary or UTF-8 URL strings) so they can be securely embedded directly into HTML or CSS files without external asset dependencies." },
        { question: "What is the difference between Base64 and UTF-8 encoding for SVG Data URIs?", answer: "Base64 encodes binary structures, creating robust strings that work anywhere but are roughly 33% larger than raw code. UTF-8 URL-encoding keeps code mostly human-readable, preserving space while requiring percent-encoding for reserved browser URL characters." },
        { question: "How do I embed an SVG Data URI in CSS styles?", answer: "Use the generated CSS snippet inside your stylesheets: `background-image: url('data:image/svg+xml;utf8,...');`. This renders the vector design as a standard, responsive CSS background asset." },
        { question: "Are my secure corporate icons transmitted to external servers?", answer: "No. All encoding, character translations, and snippet assemblies are calculated completely locally within your browser sandbox using client-side JavaScript." },
        { question: "Why should I use Data URIs instead of referencing traditional SVG file paths?", answer: "Using inline Data URIs completely eliminates separate HTTP requests for icons and assets, accelerating initial page loading speeds and simplifying single-file template packaging." },
        { question: "Are SVG Data URIs fully responsive and scalable?", answer: "Yes. Because the source is vector instructions, rendering continues to scale and preserve sharpness, matching standard SVG behavior at any zoom level." },
        { question: "What is the syntax structure of an inline SVG HTML Image tag?", answer: "Format the source attribute with your Base64 schema: `<img src='data:image/svg+xml;base64,...' alt='Icon' />`. This is fully compatible with all modern browsers." },
        { question: "Can I use SVG Data URIs in style guides or email templates?", answer: "Yes, inline vector Data URIs are widely supported in modern CSS emails, and simplify design assets because you don't need to host or attach separate graphic files." },
        { question: "Does updating the original SVG markup automatically regenerate the Data URI?", answer: "Yes, our real-time encoder monitors code inputs to dynamically compile all Data URI states, CSS backgrounds, and HTML snippets on the fly." },
        { question: "Is there a file size limit for utilizing inline Data URIs?", answer: "While browsers support large URI lengths, it is recommended to use Data URIs primarily for small icons, indicators, and decorative accents (under 10KB) to avoid bloating HTML source documents." }
      ]}
    >
      <div className="space-y-6">
        <div className="grid md:grid-cols-2 gap-6">
          {/* Input Block */}
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Source SVG Markup</Label>
              <Textarea
                value={svgInput}
                onChange={(e) => setSvgInput(e.target.value)}
                className="font-mono text-[10px] leading-relaxed min-h-[350px] bg-slate-50"
                placeholder="Paste raw SVG tag markup here..."
              />
            </div>
          </div>

          {/* Outputs Panel */}
          <div className="space-y-4 bg-white border border-slate-200 p-5 rounded-2xl shadow-xs">
            <div className="flex items-center gap-2 pb-2 border-b border-slate-100 text-rose-600 mb-2">
              <Link className="w-5 h-5" />
              <span className="font-bold text-sm uppercase tracking-wider">Generated Data URIs</span>
            </div>

            {/* Base64 URI */}
            <div className="space-y-1">
              <div className="flex justify-between items-center">
                <span className="text-[10px] font-bold uppercase text-slate-400">1. Base64 Data URI</span>
                <button
                  onClick={() => handleCopy(outputs.base64Uri, "base64")}
                  className="text-xs text-rose-600 hover:text-rose-700 font-bold flex items-center gap-1"
                >
                  {copiedType === "base64" ? "Copied!" : "Copy"}
                </button>
              </div>
              <Textarea
                value={outputs.base64Uri}
                readOnly
                className="font-mono text-[10px] leading-relaxed h-[60px] bg-slate-50 resize-none select-all"
              />
            </div>

            {/* UTF-8 URI */}
            <div className="space-y-1 pt-1">
              <div className="flex justify-between items-center">
                <span className="text-[10px] font-bold uppercase text-slate-400">2. UTF-8 URL Encoded URI</span>
                <button
                  onClick={() => handleCopy(outputs.utf8Uri, "utf8")}
                  className="text-xs text-rose-600 hover:text-rose-700 font-bold flex items-center gap-1"
                >
                  {copiedType === "utf8" ? "Copied!" : "Copy"}
                </button>
              </div>
              <Textarea
                value={outputs.utf8Uri}
                readOnly
                className="font-mono text-[10px] leading-relaxed h-[60px] bg-slate-50 resize-none select-all"
              />
            </div>

            {/* CSS background */}
            <div className="space-y-1 pt-1">
              <div className="flex justify-between items-center">
                <span className="text-[10px] font-bold uppercase text-slate-400">3. CSS Background-Image Rule</span>
                <button
                  onClick={() => handleCopy(outputs.cssBackground, "css")}
                  className="text-xs text-rose-600 hover:text-rose-700 font-bold flex items-center gap-1"
                >
                  {copiedType === "css" ? "Copied!" : "Copy"}
                </button>
              </div>
              <Textarea
                value={outputs.cssBackground}
                readOnly
                className="font-mono text-[10px] leading-relaxed h-[60px] bg-slate-50 resize-none select-all"
              />
            </div>

            {/* HTML Img tag */}
            <div className="space-y-1 pt-1">
              <div className="flex justify-between items-center">
                <span className="text-[10px] font-bold uppercase text-slate-400">4. Inline HTML &lt;img&gt; Source</span>
                <button
                  onClick={() => handleCopy(outputs.htmlImg, "html")}
                  className="text-xs text-rose-600 hover:text-rose-700 font-bold flex items-center gap-1"
                >
                  {copiedType === "html" ? "Copied!" : "Copy"}
                </button>
              </div>
              <Textarea
                value={outputs.htmlImg}
                readOnly
                className="font-mono text-[10px] leading-relaxed h-[60px] bg-slate-50 resize-none select-all"
              />
            </div>
          </div>
        </div>

        {/* Visual Render stage of the data URI */}
        {outputs.base64Uri && (
          <div className="bg-slate-50 border border-slate-100 rounded-2xl p-5 space-y-3">
            <h4 className="font-bold text-sm uppercase tracking-wider text-slate-800">Inline Data URI Rendering Test</h4>
            <div className="flex justify-center items-center min-h-[150px] bg-white border border-slate-200 rounded-xl p-4 overflow-hidden checkerboard-bg">
              {/* Load inline using image tag with generated base64 */}
              <img
                src={outputs.base64Uri}
                alt="Inline preview"
                className="max-w-[120px] max-h-[120px]"
                referrerPolicy="no-referrer"
              />
            </div>
            <p className="text-[11px] text-slate-500 text-center leading-relaxed">
              Successfully rendered locally directly from the computed Base64 string source.
            </p>
          </div>
        )}
      </div>
    </ToolPageLayout>
  );
}
